import { FC, useState } from "react"
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Add, DeleteOutline } from "@mui/icons-material"
import { UserbotCatalog } from "../../interfaces"
import { isValidJson } from "../common/jsonUtils"
import { rawFromFieldValue } from "./fieldSpecCoerce"
import { validateFieldValues } from "./fieldSpecValidate"

export interface FieldOption {
  value: string
  label: string
}

interface IProps {
  spec: UserbotCatalog.FieldSpec
  value: unknown
  onChange: (value: unknown) => void
  /** Опции для select-типов (готовит FieldSpecForm). */
  options?: FieldOption[]
  /** Пресеты cron из каталога. */
  cronPresets?: UserbotCatalog.CronPreset[]
  /** Подсказки голов для templatable-полей. */
  headHints?: string[]
  error?: string
}

/** Whole-string плейсхолдер. */
const isPlaceholder = (v: string): boolean => /^\{\{[\s\S]+\}\}$/.test(v)

/** Находит индекс варианта oneOf, соответствующего значению. */
const matchVariantIndex = (
  variants: UserbotCatalog.FieldSpec[],
  value: unknown,
): number => {
  for (let i = variants.length - 1; i >= 0; i--) {
    const variant = variants[i]
    const raw = rawFromFieldValue(variant, value)
    const errs = validateFieldValues([variant], { [variant.key]: raw })
    if (!Object.keys(errs).length) return i
  }
  return 0
}

interface TemplatableInputProps {
  label: string
  value: string
  onChange: (value: string) => void
  headHints: string[]
  error?: string
  help?: string
  multiline?: boolean
  minRows?: number
  isTemplate?: boolean
}

/** Поле с автокомплитом по {{...}}-плейсхолдерам (для spec.templatable). */
const TemplatableInput: FC<TemplatableInputProps> = ({
  label,
  value,
  onChange,
  headHints,
  error,
  help,
  multiline,
  minRows,
  isTemplate,
}) => {
  if (!headHints.length) {
    return (
      <TextField
        label={label}
        value={value}
        fullWidth
        multiline={multiline}
        minRows={minRows}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        helperText={error ?? help}
        InputProps={
          isTemplate ? { style: { fontFamily: "monospace" } } : undefined
        }
      />
    )
  }

  // Контролируем только inputValue: при controlled value MUI считает
  // введённый текст «выбранным вариантом» и перестаёт фильтровать опции.
  return (
    <Autocomplete
      freeSolo
      options={headHints}
      inputValue={value}
      onInputChange={(_, v, reason) => {
        if (reason === "input" || reason === "clear") {
          onChange(v)
        }
      }}
      onChange={(_, v) => {
        if (v) onChange(v)
      }}
      filterOptions={(options, state) =>
        state.inputValue.startsWith("{{")
          ? options.filter((o) =>
              o.toLowerCase().includes(state.inputValue.toLowerCase()),
            )
          : []
      }
      fullWidth
      renderInput={(p) => (
        <TextField
          {...p}
          label={label}
          multiline={multiline}
          minRows={minRows}
          error={!!error}
          helperText={error ?? help}
          InputProps={{
            ...p.InputProps,
            style: isTemplate ? { fontFamily: "monospace" } : undefined,
          }}
        />
      )}
    />
  )
}

export const FieldSpecInput: FC<IProps> = ({
  spec,
  value,
  onChange,
  options = [],
  cronPresets = [],
  headHints = [],
  error,
}) => {
  const str = (value ?? "") as string
  const label = spec.label + (spec.required ? " *" : "")
  const isTemplate = typeof value === "string" && isPlaceholder(value)

  // Индекс выбранного варианта для oneOf (hooks rules: always called)
  const [selectedVariant, setSelectedVariant] = useState(() =>
    spec.type === "oneOf" && spec.variants
      ? matchVariantIndex(spec.variants, value)
      : 0,
  )

  const templatableIcon = spec.templatable ? (
    <Tooltip title="Можно вставить плейсхолдер {{...}}" arrow>
      <Chip
        size="small"
        label="{{}}"
        sx={{ fontFamily: "monospace", cursor: "help" }}
      />
    </Tooltip>
  ) : null

  switch (spec.type) {
    case "boolean":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={value === true || value === "true"}
              onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            />
          }
          label={spec.label}
        />
      )

    case "text":
      return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {spec.templatable ? (
            <TemplatableInput
              label={label}
              value={str}
              onChange={onChange}
              headHints={headHints}
              error={error}
              help={spec.help}
              multiline
              minRows={3}
              isTemplate={isTemplate}
            />
          ) : (
            <TextField
              label={label}
              value={str}
              fullWidth
              multiline
              minRows={3}
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              helperText={error ?? spec.help}
              InputProps={
                isTemplate ? { style: { fontFamily: "monospace" } } : undefined
              }
            />
          )}
          {templatableIcon}
        </Stack>
      )

    case "number":
      return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {spec.templatable ? (
            <TemplatableInput
              label={label}
              value={str}
              onChange={onChange}
              headHints={headHints}
              error={error}
              help={spec.help}
              isTemplate={isTemplate}
            />
          ) : (
            <TextField
              label={label}
              value={str}
              fullWidth
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              helperText={error ?? spec.help}
              InputProps={
                isTemplate ? { style: { fontFamily: "monospace" } } : undefined
              }
            />
          )}
          {templatableIcon}
        </Stack>
      )

    case "json": {
      const invalid = str.trim() !== "" && !isValidJson(str)
      return (
        <TextField
          label={label}
          value={str}
          fullWidth
          multiline
          minRows={3}
          onChange={(e) => onChange(e.target.value)}
          error={!!error || invalid}
          helperText={error ?? (invalid ? "Некорректный JSON" : spec.help)}
          InputProps={{ style: { fontFamily: "monospace" } }}
        />
      )
    }

    case "enum":
      return (
        <TextField
          select
          label={label}
          value={str}
          fullWidth
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={error ?? spec.help}
        >
          {(spec.enumValues ?? []).map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </TextField>
      )

    case "clientId":
    case "role":
    case "entity":
    case "llmTask":
    case "matcherType": {
      // select с опциями из optionsSource; entity/matcherType допускают свободный ввод
      const freeSolo = spec.type === "entity" || spec.type === "matcherType"
      return (
        <Autocomplete
          freeSolo={freeSolo}
          options={options.map((o) => o.value)}
          value={str}
          onChange={(_, v) => onChange(v ?? "")}
          onInputChange={freeSolo ? (_, v) => onChange(v) : undefined}
          getOptionLabel={(v) =>
            options.find((o) => o.value === v)?.label ?? String(v)
          }
          renderInput={(p) => (
            <TextField
              {...p}
              label={label}
              error={!!error}
              helperText={error ?? spec.help}
            />
          )}
        />
      )
    }

    case "chatId":
      return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {spec.templatable ? (
            <TemplatableInput
              label={label}
              value={str}
              onChange={onChange}
              headHints={headHints}
              error={error}
              help={spec.help ?? "id / @username / invite-ссылка"}
              isTemplate={isTemplate}
            />
          ) : (
            <TextField
              label={label}
              value={str}
              fullWidth
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              helperText={
                error ?? spec.help ?? "id / @username / invite-ссылка"
              }
              InputProps={
                isTemplate ? { style: { fontFamily: "monospace" } } : undefined
              }
            />
          )}
          {templatableIcon}
        </Stack>
      )

    case "cron":
      return (
        <Box>
          <TextField
            label={label}
            value={str}
            fullWidth
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={error ?? spec.help}
          />
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
            {cronPresets.map((p) => (
              <Chip
                key={p.cron}
                size="small"
                variant="outlined"
                label={`${p.label} (${p.cron})`}
                onClick={() => onChange(p.cron)}
              />
            ))}
          </Stack>
        </Box>
      )

    case "array": {
      const arr = Array.isArray(value) ? (value as unknown[]) : []
      const item = spec.item
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {spec.label}
            {spec.required ? " *" : ""}
          </Typography>
          <Stack spacing={1} mt={1}>
            {arr.map((el, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Box flexGrow={1}>
                  {item ? (
                    <FieldSpecInput
                      spec={{ ...item, label: `#${i + 1}` }}
                      value={el}
                      options={options}
                      headHints={headHints}
                      onChange={(v) => {
                        const next = [...arr]
                        next[i] = v
                        onChange(next)
                      }}
                    />
                  ) : null}
                </Box>
                <IconButton
                  color="error"
                  onClick={() => onChange(arr.filter((_, j) => j !== i))}
                >
                  <DeleteOutline />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  onChange([...arr, item?.type === "boolean" ? "false" : ""])
                }
              >
                Добавить
              </Button>
            </Box>
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    case "object": {
      const obj = (value ?? {}) as Record<string, unknown>
      const fields = spec.fields ?? []
      return (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              {spec.label}
              {spec.required ? " *" : ""}
            </Typography>
            {templatableIcon}
          </Stack>
          <Stack
            spacing={2}
            pl={2}
            sx={{ borderLeft: 1, borderColor: "divider" }}
          >
            {fields.map((f) => (
              <FieldSpecInput
                key={f.key}
                spec={f}
                value={obj[f.key]}
                options={options}
                cronPresets={cronPresets}
                headHints={headHints}
                error={error}
                onChange={(v) => onChange({ ...obj, [f.key]: v })}
              />
            ))}
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    case "oneOf": {
      const variants = spec.variants ?? []
      const activeVariant = variants[selectedVariant] ?? variants[0]

      return (
        <Box>
          <FormControl>
            <FormLabel error={!!error}>
              {spec.label}
              {spec.required ? " *" : ""}
            </FormLabel>
            <RadioGroup
              row
              value={selectedVariant}
              onChange={(_, v) => {
                const idx = Number(v)
                setSelectedVariant(idx)
                const variant = variants[idx]
                if (variant) {
                  onChange(
                    variant.type === "boolean"
                      ? "false"
                      : variant.type === "array"
                        ? []
                        : variant.type === "object"
                          ? {}
                          : "",
                  )
                }
              }}
            >
              {variants.map((v, i) => (
                <FormControlLabel
                  key={v.key}
                  value={i}
                  control={<Radio size="small" />}
                  label={v.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {activeVariant && (
            <Box mt={1} pl={2} sx={{ borderLeft: 1, borderColor: "divider" }}>
              <FieldSpecInput
                spec={{ ...activeVariant, label: activeVariant.label }}
                value={value}
                options={options}
                cronPresets={cronPresets}
                headHints={headHints}
                error={error}
                onChange={onChange}
              />
            </Box>
          )}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    default: // string
      return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {spec.templatable ? (
            <TemplatableInput
              label={label}
              value={str}
              onChange={onChange}
              headHints={headHints}
              error={error}
              help={spec.help}
              isTemplate={isTemplate}
            />
          ) : (
            <TextField
              label={label}
              value={str}
              fullWidth
              onChange={(e) => onChange(e.target.value)}
              error={!!error}
              helperText={error ?? spec.help}
              InputProps={
                isTemplate ? { style: { fontFamily: "monospace" } } : undefined
              }
            />
          )}
          {templatableIcon}
        </Stack>
      )
  }
}
